/**
 * Task #599 — Lead detail "AI Catcher Conversation" card.
 *
 * Renders inside the existing Lead Detail dialog as a tabbed surface so
 * a rep can flip between the running transcript, the AI's qualification
 * snapshot (captured fields + score that came out of the standard
 * lead-qualification engine after handoff), and any hard-stop
 * redactions the engine had to perform on the AI's drafts.
 *
 * Renders nothing when the lead wasn't created by the catcher so legacy
 * leads stay clean — this lets the parent component drop the card in
 * unconditionally without flickering an empty card.
 */
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldAlert, Sparkles, MessageSquare, ListChecks } from "lucide-react";

type Message = {
  id: string;
  role: "ai" | "prospect" | "system";
  body: string;
  createdAt: string;
  action?: string | null;
};
type Violation = { id: string; kind: string; pattern: string; snippet: string };
type Conversation = {
  id: string;
  channel: string;
  outcome: string;
  capturedEmail: string | null;
  capturedName: string | null;
  capturedPhone: string | null;
  capturedEventDate: string | null;
  capturedEventType: string | null;
  capturedExtrasJson: Record<string, string | null> | null;
  aiSummary: string | null;
  totalTokens: number;
  totalCostCents: number;
  createdAt: string;
};

type Payload = {
  conversation: Conversation | null;
  messages: Message[];
  violations: Violation[];
};

type QualBreakdown = {
  qualificationRating: number | null;
  ratingLabel: string | null;
  ratingSummary: string | null;
  qualificationStatus: string | null;
};

const CHANNEL_LABEL: Record<string, string> = {
  web: "Web chat",
  sms: "SMS",
  missed_call: "Missed call",
  instagram: "Instagram DM",
  messenger: "Messenger",
  form: "Contact form",
};

function outcomeVariant(o: string): "default" | "secondary" | "destructive" {
  if (o === "handoff") return "default";
  if (o === "blocked" || o === "errored") return "destructive";
  return "secondary";
}

function ratingPillClass(label: string | null | undefined): string {
  switch ((label || "").toLowerCase()) {
    case "strong":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200";
    case "good":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200";
    case "fair":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200";
    case "low":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200";
    case "unqualified":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function LeadCatcherConversationCard({ leadId }: { leadId: string }) {
  // Conversation transcript + violations.
  const q = useQuery<Payload>({
    queryKey: ["/api/leads", leadId, "lead-catcher-conversation"],
    queryFn: async () => {
      const r = await fetch(`/api/leads/${leadId}/lead-catcher-conversation`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  // Standard qualification snapshot — same endpoint
  // QualificationBreakdownDialog uses, so the score the rep sees here
  // matches the score everywhere else in the CRM.
  const conv = q.data?.conversation;
  const qualQ = useQuery<QualBreakdown>({
    queryKey: ["/api/leads", leadId, "qualification"],
    queryFn: async () => {
      const r = await fetch(`/api/leads/${leadId}/qualification`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    enabled: !!conv,
  });

  if (q.isLoading) return null;
  if (!conv) return null;

  const violations = q.data?.violations ?? [];
  const messages = q.data?.messages ?? [];
  const extras = (conv.capturedExtrasJson || {}) as Record<string, string | null>;
  const channelLabel = CHANNEL_LABEL[conv.channel] || conv.channel;

  return (
    <Card data-testid="card-lead-catcher-conversation">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" />
          AI Catcher Conversation
          <Badge variant="secondary" data-testid="text-catcher-channel">{channelLabel}</Badge>
          <Badge variant={outcomeVariant(conv.outcome)} data-testid="text-catcher-outcome">
            {conv.outcome}
          </Badge>
          {qualQ.data?.ratingLabel && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${ratingPillClass(qualQ.data.ratingLabel)}`}
              data-testid="text-catcher-qualification-rating"
            >
              {qualQ.data.ratingLabel}
              {typeof qualQ.data.qualificationRating === "number" && ` · ${qualQ.data.qualificationRating}/5`}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="transcript" className="w-full">
          <TabsList className="grid w-full grid-cols-3" data-testid="tabs-catcher">
            <TabsTrigger value="transcript" data-testid="tab-catcher-transcript">
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
              Transcript
            </TabsTrigger>
            <TabsTrigger value="qualification" data-testid="tab-catcher-qualification">
              <ListChecks className="mr-1.5 h-3.5 w-3.5" />
              Qualification
            </TabsTrigger>
            <TabsTrigger value="redactions" data-testid="tab-catcher-redactions">
              <ShieldAlert className="mr-1.5 h-3.5 w-3.5" />
              Hard-stops
              {violations.length > 0 && (
                <Badge variant="destructive" className="ml-1.5 h-4 px-1 text-[10px]">
                  {violations.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="transcript" className="mt-3">
            <div
              className="max-h-72 space-y-2 overflow-y-auto rounded-md border p-3"
              data-testid="region-catcher-transcript"
            >
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground">No messages yet.</p>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.role === "prospect" ? "justify-end" : "justify-start"}`}
                    data-testid={`row-message-${m.id}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                        m.role === "prospect"
                          ? "bg-primary text-primary-foreground"
                          : m.role === "ai"
                          ? "bg-muted"
                          : "bg-yellow-100 text-yellow-900 dark:bg-yellow-900/40 dark:text-yellow-100"
                      }`}
                    >
                      <div className="text-[11px] uppercase opacity-70">{m.role}</div>
                      <div className="whitespace-pre-wrap">{m.body}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="qualification" className="mt-3 space-y-3">
            {conv.aiSummary && (
              <div
                className="rounded-md bg-muted p-3 text-sm whitespace-pre-wrap"
                data-testid="text-catcher-summary"
              >
                {conv.aiSummary}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              <div data-testid="text-catcher-name">
                <span className="text-muted-foreground">Name:</span> {conv.capturedName || "—"}
              </div>
              <div data-testid="text-catcher-email">
                <span className="text-muted-foreground">Email:</span> {conv.capturedEmail || "—"}
              </div>
              <div data-testid="text-catcher-phone">
                <span className="text-muted-foreground">Phone:</span> {conv.capturedPhone || "—"}
              </div>
              <div data-testid="text-catcher-event-type">
                <span className="text-muted-foreground">Event:</span> {conv.capturedEventType || "—"}
              </div>
              <div data-testid="text-catcher-event-date">
                <span className="text-muted-foreground">Date:</span> {conv.capturedEventDate || "—"}
              </div>
              <div data-testid="text-catcher-tokens">
                <span className="text-muted-foreground">Tokens:</span> {conv.totalTokens.toLocaleString()}
              </div>
              {Object.entries(extras).map(([k, v]) =>
                v ? (
                  <div key={k} data-testid={`text-catcher-extra-${k}`}>
                    <span className="text-muted-foreground">{k}:</span> {v}
                  </div>
                ) : null,
              )}
            </div>
            {qualQ.data?.ratingSummary && (
              <div
                className="rounded-md border bg-card p-3 text-sm"
                data-testid="text-catcher-qual-summary"
              >
                <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  Qualification summary
                </div>
                {qualQ.data.ratingSummary}
              </div>
            )}
          </TabsContent>

          <TabsContent value="redactions" className="mt-3">
            {violations.length === 0 ? (
              <p
                className="rounded-md border border-dashed p-3 text-sm text-muted-foreground"
                data-testid="text-catcher-no-violations"
              >
                No hard-stop redactions on this conversation.
              </p>
            ) : (
              <ul className="space-y-2">
                {violations.map((v) => (
                  <li
                    key={v.id}
                    className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs"
                    data-testid={`row-violation-${v.id}`}
                  >
                    <div className="mb-1 flex items-center gap-1 font-medium text-destructive">
                      <ShieldAlert className="h-3.5 w-3.5" />
                      <Badge variant="outline" className="border-destructive/40">{v.kind}</Badge>
                      <span className="text-muted-foreground">— {v.pattern}</span>
                    </div>
                    <code className="text-[11px]">{v.snippet}</code>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
